import { create } from 'zustand';
import type { ApiError, LoginRequest, Role } from '@ste/shared';
import { API_ROOT, siteBase, useConnection } from './connection.ts';

// Access token lives in memory only. The refresh token is persisted so a reload keeps
// the session (web: localStorage; native apps switch to secure storage in Phase 7).

const REFRESH_KEY = 'ste.refreshToken';
/**
 * Sessions without a refresh token (emergency login on the Pi, ADR-0016) survive a reload
 * of the tab until they expire; closing the tab ends them.
 */
const TAB_SESSION_KEY = 'ste.tabSession';

interface TabSession {
  accessToken: string;
  expiresAt: number;
  user: AuthUser;
}

const tabSession = {
  get(): TabSession | undefined {
    try {
      const raw = sessionStorage.getItem(TAB_SESSION_KEY);
      const session = raw ? (JSON.parse(raw) as TabSession) : undefined;
      return session && session.expiresAt > Date.now() ? session : undefined;
    } catch {
      return undefined;
    }
  },
  set(session: TabSession | undefined): void {
    try {
      if (session) sessionStorage.setItem(TAB_SESSION_KEY, JSON.stringify(session));
      else sessionStorage.removeItem(TAB_SESSION_KEY);
    } catch {
      // storage unavailable: the session ends on reload
    }
  },
};

/** gateway accounts have a role; cloud accounts a global role (roles per site come from /sites) */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role?: Role;
  globalRole?: 'installer' | 'admin' | null;
  totpEnabled: boolean;
}

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

export interface TokenStorage {
  get(): string | null;
  set(token: string | null): void;
}

export const localTokenStorage: TokenStorage = {
  get: () => {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      if (token) localStorage.setItem(REFRESH_KEY, token);
      else localStorage.removeItem(REFRESH_KEY);
    } catch {
      // storage unavailable (private mode): session ends on reload
    }
  },
};

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiError['error'];
  /** with code totp_setup_required (STE Connect): allows only setting up TOTP */
  readonly setupToken: string | undefined;

  constructor(status: number, body: Partial<ApiError> & { setupToken?: unknown }) {
    super(body.message ?? `HTTP ${status}`);
    this.status = status;
    this.code = body.error ?? 'internal';
    this.setupToken = typeof body.setupToken === 'string' ? body.setupToken : undefined;
  }
}

type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | undefined;
  accessToken: string | undefined;
  /** epoch ms when the access token expires */
  expiresAt: number;
  login(request: LoginRequest): Promise<void>;
  /** takes over tokens obtained elsewhere (e.g. accepting an invitation) */
  adopt(tokens: TokenResponse): void;
  logout(): Promise<void>;
  /** returns a valid access token, refreshing if needed; undefined when logged out */
  getAccessToken(): Promise<string | undefined>;
  restore(): Promise<void>;
}

let storage: TokenStorage = localTokenStorage;
const beforeLogout: (() => Promise<void>)[] = [];

/** runs while the session is still valid, e.g. to remove this phone's push token */
export function onBeforeLogout(task: () => Promise<void>): void {
  beforeLogout.push(task);
}
let refreshing: Promise<string | undefined> | undefined;

export function setTokenStorage(next: TokenStorage): void {
  storage = next;
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok)
    throw new ApiRequestError(res.status, (await res.json().catch(() => ({}))) as Partial<ApiError>);
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export const useAuth = create<AuthState>((set, get) => {
  const apply = (tokens: TokenResponse) => {
    storage.set(tokens.refreshToken);
    // refresh 30 s before expiry
    const expiresAt = Date.now() + (tokens.expiresIn - 30) * 1000;
    tabSession.set(
      tokens.refreshToken ? undefined : { accessToken: tokens.accessToken, expiresAt, user: tokens.user },
    );
    set({ status: 'authenticated', user: tokens.user, accessToken: tokens.accessToken, expiresAt });
  };

  const clear = () => {
    storage.set(null);
    tabSession.set(undefined);
    useConnection.getState().reset();
    set({ status: 'anonymous', user: undefined, accessToken: undefined, expiresAt: 0 });
  };

  const refresh = (): Promise<string | undefined> => {
    // single flight: parallel requests share one refresh
    refreshing ??= (async () => {
      const refreshToken = storage.get();
      if (!refreshToken) {
        clear();
        return undefined;
      }
      try {
        const tokens = await postJson<TokenResponse>('/auth/refresh', { refreshToken });
        apply(tokens);
        return tokens.accessToken;
      } catch (error) {
        // network errors keep the session; rejected tokens end it
        if (error instanceof ApiRequestError) clear();
        return undefined;
      } finally {
        refreshing = undefined;
      }
    })();
    return refreshing;
  };

  return {
    status: 'unknown',
    user: undefined,
    accessToken: undefined,
    expiresAt: 0,

    async login(request) {
      apply(await postJson<TokenResponse>('/auth/login', request));
    },

    adopt(tokens) {
      apply(tokens);
    },

    async logout() {
      // best effort, never blocks the logout for long
      await Promise.race([
        Promise.allSettled(beforeLogout.map((task) => task())),
        new Promise((resolve) => setTimeout(resolve, 3000)),
      ]);
      const refreshToken = storage.get();
      clear();
      if (refreshToken) await postJson('/auth/logout', { refreshToken }).catch(() => undefined);
    },

    async getAccessToken() {
      const { accessToken, expiresAt } = get();
      if (accessToken && Date.now() < expiresAt) return accessToken;
      return refresh();
    },

    async restore() {
      if (!storage.get()) {
        const session = tabSession.get();
        set(session ? { status: 'authenticated', ...session } : { status: 'anonymous' });
        return;
      }
      await refresh();
      if (!get().accessToken && get().status === 'unknown') set({ status: 'anonymous' });
    },
  };
});

/** site-scoped call: /api/v1 on the gateway, /api/v1/sites/{id} on STE Connect */
export function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return authedFetch<T>(`${siteBase()}${path}`, init);
}

/** account-level call, always below /api/v1 */
export function rootFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return authedFetch<T>(`${API_ROOT}${path}`, init);
}

/**
 * site-scoped file download (maintenance, ADR-0021): fetched with the token, then saved via a
 * temporary link; the file name comes from Content-Disposition
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const res = await authedResponse(`${siteBase()}${path}`);
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** fetch with authentication and one retry after a refresh */
async function authedFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await authedResponse(url, init);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** response of an authenticated request; throws ApiRequestError when not ok */
async function authedResponse(url: string, init: RequestInit = {}): Promise<Response> {
  const send = async (token: string | undefined) =>
    fetch(url, {
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });

  const auth = useAuth.getState();
  let res = await send(await auth.getAccessToken());
  if (res.status === 401) {
    useAuth.setState({ expiresAt: 0 });
    const token = await useAuth.getState().getAccessToken();
    if (token) res = await send(token);
  }
  if (!res.ok) {
    throw new ApiRequestError(res.status, (await res.json().catch(() => ({}))) as Partial<ApiError>);
  }
  return res;
}
